/**
 * Profile Page
 *
 * Allows crew members to view and edit their profile details and change their password.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import {
  updateProfile,
  changePassword,
  getAuthMethod,
  type UpdateProfileRequest,
  type ChangePasswordRequest,
} from '../../api/auth';
import { getErrorMessage } from '../../api/client';
import SmsNotificationSettings from '../../components/profile/SmsNotificationSettings';
import NotificationPreferenceSettings from '../../components/profile/NotificationPreferenceSettings';
import EventReminderSettings from '../../components/profile/EventReminderSettings';
import CalendarSubscriptions from '../../components/profile/CalendarSubscriptions';
import InstallPrompt from '../../components/pwa/InstallPrompt';
import EnablePushCard from '../../components/pwa/EnablePushCard';

const roleLabels: Record<string, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  CREW: 'Crew Member',
};

export default function ProfilePage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const { currentOrg } = useOrganization();

  // Signed in with Google → no password form. A Google user may have no
  // password they've ever seen (admin-created accounts get a random one),
  // so "Current Password" would be an unanswerable question.
  const isGoogleSession = getAuthMethod() === 'google';

  // Profile form state
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const getInitials = (first: string, last: string) => {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError(null);
    setProfileSuccess(null);
    setProfileSaving(true);

    try {
      const data: UpdateProfileRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || undefined,
      };

      await updateProfile(data);
      await refreshUser();
      setProfileSuccess('Profile updated successfully');

      setTimeout(() => setProfileSuccess(null), 3000);
    } catch (err) {
      setProfileError(getErrorMessage(err));
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(null);

    // Validation
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordSaving(true);

    try {
      const data: ChangePasswordRequest = {
        currentPassword,
        newPassword,
        confirmPassword,
      };

      await changePassword(data);
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      setTimeout(() => setPasswordSuccess(null), 3000);
    } catch (err) {
      setPasswordError(getErrorMessage(err));
    } finally {
      setPasswordSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Profile Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          {user.profilePhoto ? (
            <img
              src={user.profilePhoto}
              alt={`${user.firstName} ${user.lastName}`}
              className="w-16 h-16 object-cover"
            />
          ) : (
            <div className="w-16 h-16 bg-primary-100 text-primary-700 flex items-center justify-center text-xl font-semibold">
              {getInitials(user.firstName, user.lastName)}
            </div>
          )}
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {user.firstName} {user.lastName}
            </h1>
            <p className="text-sm text-gray-500">{user.email}</p>
            <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
              {currentOrg?.role ? roleLabels[currentOrg.role] || currentOrg.role : 'No Organization'}
            </span>
          </div>
        </div>
      </div>

      {/* Edit Profile Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Edit Profile</h2>

        <form onSubmit={handleProfileSubmit} className="space-y-4">
          {profileError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {profileError}
            </div>
          )}

          {profileSuccess && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
              {profileSuccess}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
                required
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={user.email}
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">Contact an administrator to change your email</p>
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
            />
            {/* This field is contact info only. Saving it here never enables SMS —
                that requires the separate, verified opt-in below. Say so, or people
                will reasonably assume adding a number here means they'll be texted. */}
            <p className="mt-1 text-xs text-gray-500">
              Contact information for your organization. To receive text message
              notifications, use{' '}
              <a href="#sms-notifications" className="text-primary-600 hover:underline">
                Text Message Notifications
              </a>{' '}
              below.
            </p>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={profileSaving}
              className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {profileSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* The ProgramRow app: install + device notifications (PWA). Cards
          self-hide when installed/enabled/dismissed or unsupported. */}
      <div className="space-y-3">
        <InstallPrompt />
        <EnablePushCard />
      </div>

      {/* Per-category email/text/push notification toggles */}
      <NotificationPreferenceSettings />

      {/* Google-Calendar-style crew-call reminders (up to 2, global) */}
      <EventReminderSettings />

      {/* Subscribe to the schedule from Google/Apple/Outlook (Enterprise orgs) */}
      <CalendarSubscriptions />

      {/* SMS opt-in. Self-service only — see the component for why. */}
      <SmsNotificationSettings />

      {/* Change Password Form — hidden for Google sessions */}
      {isGoogleSession ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Password</h2>
          <p className="text-sm text-gray-600">
            You're signed in with Google, so there's no password to manage here.
            If you'd also like to sign in with an email and password, use
            &ldquo;Forgot password&rdquo; on the login page to set one.
          </p>
        </div>
      ) : (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h2>

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          {passwordError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {passwordError}
            </div>
          )}

          {passwordSuccess && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
              {passwordSuccess}
            </div>
          )}

          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
              required
            />
          </div>

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
              required
              minLength={8}
            />
            <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-rack focus:border-rack"
              required
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={passwordSaving}
              className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {passwordSaving ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
      )}

      {/* Account Info */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>

        <dl className="space-y-3">
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Member Since</dt>
            <dd className="text-sm text-gray-900">{formatDate(user.createdAt)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Last Login</dt>
            <dd className="text-sm text-gray-900">{formatDate(user.lastLoginAt)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-500">Account Status</dt>
            <dd className="text-sm">
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700">
                Active
              </span>
            </dd>
          </div>
        </dl>
      </div>

      {/* Phones only: the crew header has no room for Sign out below sm. */}
      <button
        type="button"
        onClick={() => {
          logout();
          navigate('/login');
        }}
        className="sm:hidden w-full px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        Sign out
      </button>
    </div>
  );
}
